import { type ReactNode, useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ChangeShipmentStatusDto,
  type Locale,
  type OrderStatusCode,
  type StatusTransitionResponse,
} from '@aps/contracts';
import { Button, useToast } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';

interface StatusChangerProps {
  shipmentId: string;
  orderId: string;
  locale: Locale;
}

/**
 * Status o'zgartirish.
 *
 * Tugmalar SERVERDAN kelgan ro'yxatdan quriladi — klientda status grafi
 * YOZILMAYDI. Graf `status_transitions` jadvalida va u admin panelda
 * sozlanadi (avtopart-status-machine skill).
 *
 * `requiresComment` va `requiresEta` ham o'sha jadvaldan keladi: qaysi
 * o'tishda izoh majburiyligi kodda qattiq yozilmaydi.
 */
export function StatusChanger({ shipmentId, orderId, locale }: StatusChangerProps): ReactNode {
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const commentId = useId();
  const etaFromId = useId();
  const etaToId = useId();

  const [selected, setSelected] = useState<OrderStatusCode | null>(null);
  const [comment, setComment] = useState('');
  const [etaFrom, setEtaFrom] = useState('');
  const [etaTo, setEtaTo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const transitions = useQuery({
    queryKey: ['transitions', shipmentId],
    queryFn: () => api.shipmentTransitions(shipmentId),
  });

  // Jo'natma almashsa tanlov qolib ketmasin.
  useEffect(() => {
    setSelected(null);
    setComment('');
    setEtaFrom('');
    setEtaTo('');
    setError(null);
  }, [shipmentId]);

  const change = useMutation({
    mutationFn: (dto: ChangeShipmentStatusDto) => api.changeShipmentStatus(shipmentId, dto),
    onSuccess: (_data, dto) => {
      setSelected(null);
      setComment('');
      setEtaFrom('');
      setEtaTo('');
      setError(null);
      /*
       * Push KETDIMI — operator uchun qaror ma'lumoti.
       *
       * Push yubormaydigan statusdan keyin mijozga operatorning o'zi
       * qo'ng'iroq qilishi kerak. Ilgari ekranda bu ko'rinmasdi va
       * operator mijoz xabardor deb o'ylab o'tib ketardi.
       */
      const moved = transitions.data?.find((row) => row.toCode === dto.toCode);
      toast.show(
        moved?.toNotify === true ? t.toastStatusPush : t.toastStatusNoPush,
        moved?.toNotify === true ? 'success' : 'info',
      );
      // Buyurtma, o'tishlar, TARIX va ish navbati — hammasi eskirdi.
      //
      // `shipment` kalitini unutish oson va u JIMGINA noto'g'ri ko'rinadi:
      // status o'zgargani ko'rinadi, tarix esa eski holicha "bo'sh" bo'lib
      // turadi. Shu holat brauzerda ushlangan.
      void client.invalidateQueries({ queryKey: ['order', orderId] });
      void client.invalidateQueries({ queryKey: ['shipment', shipmentId] });
      void client.invalidateQueries({ queryKey: ['transitions', shipmentId] });
      void client.invalidateQueries({ queryKey: ['work-queue'] });
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  if (transitions.isPending) return <p className="aps-muted">{t.loading}</p>;
  if (transitions.isError) return <p className="aps-error">{t.errorTitle}</p>;
  if (transitions.data.length === 0) {
    return <p className="aps-muted">{t.statusNoTransitions}</p>;
  }

  const active: StatusTransitionResponse | undefined = transitions.data.find(
    (transition) => transition.toCode === selected,
  );

  const submit = (): void => {
    if (active === undefined) return;

    // Majburiy maydonlarni SERVERDAN kelgan bayroqlarga qarab tekshiramiz.
    // Server ham tekshiradi — bu foydalanuvchini bekorga so'rov
    // yubortirmaslik uchun.
    if (active.requiresComment && comment.trim() === '') {
      setError(t.statusCommentRequired);
      return;
    }
    if (active.requiresEta && etaFrom === '') {
      setError(t.statusEtaRequired);
      return;
    }

    change.mutate({
      toCode: active.toCode,
      ...(comment.trim() === '' ? {} : { comment: comment.trim() }),
      ...(etaFrom === '' ? {} : { newEtaFrom: new Date(etaFrom).toISOString() }),
      ...(etaTo === '' ? {} : { newEtaTo: new Date(etaTo).toISOString() }),
    });
  };

  return (
    <div className="adm-status">
      <div className="adm-status__options">
        {transitions.data.map((transition) => (
          <Button
            key={transition.toCode}
            size="sm"
            variant={selected === transition.toCode ? 'primary' : 'secondary'}
            onClick={() => {
              setSelected(transition.toCode);
              setError(null);
            }}
          >
            {locale === 'RU'
              ? (transition.toLabelRu ?? transition.toLabelUz)
              : transition.toLabelUz}
          </Button>
        ))}
      </div>

      {active !== undefined && (
        <div className="adm-status__form">
          {active.requiresComment && (
            <div className="aps-field">
              <label className="aps-field__label" htmlFor={commentId}>
                {t.statusComment}
              </label>
              <input
                id={commentId}
                className="aps-field__input"
                value={comment}
                onChange={(event) => {
                  setComment(event.target.value);
                }}
              />
            </div>
          )}

          {active.requiresEta && (
            <>
              <div className="aps-field">
                <label className="aps-field__label" htmlFor={etaFromId}>
                  {t.statusEtaFrom}
                </label>
                <input
                  id={etaFromId}
                  className="aps-field__input"
                  type="date"
                  value={etaFrom}
                  onChange={(event) => {
                    setEtaFrom(event.target.value);
                  }}
                />
              </div>
              <div className="aps-field">
                <label className="aps-field__label" htmlFor={etaToId}>
                  {t.statusEtaTo}
                </label>
                <input
                  id={etaToId}
                  className="aps-field__input"
                  type="date"
                  value={etaTo}
                  onChange={(event) => {
                    setEtaTo(event.target.value);
                  }}
                />
              </div>
            </>
          )}

          {error !== null && (
            <p className="aps-error" role="alert">
              {error}
            </p>
          )}

          <Button size="sm" onClick={submit} disabled={change.isPending}>
            {change.isPending ? t.statusApplying : t.statusApply}
          </Button>
        </div>
      )}
    </div>
  );
}
